import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from './api'

export type IntelligenceDashboard = {
  academicSession: string
  openGaps: number
  activeInterventions: number
  pendingOutcomeMappings: number
  methodology: string
}

export type OutcomeAttainment = {
  learningOutcomeId: string
  code: string
  description: string
  status: string
  attainmentPercent: number | null
  evidenceCount: number
  confidence: string
  explanation: string[]
}

const base = '/acdmc/intelligence'

export const fetchIntelligenceDashboard = () =>
  api.get<{ success: boolean; data: IntelligenceDashboard }>(`${base}/dashboard`).then((r) => r.data.data)

export const fetchAttainment = (params: { className: string; section?: string; subjectName: string }) =>
  api
    .get<{ success: boolean; data: { attainments: OutcomeAttainment[]; teachingCoverage: Record<string, unknown> } }>(
      `${base}/attainment`,
      { params },
    )
    .then((r) => r.data.data)

export const fetchGaps = (params: { className: string; section?: string; subjectName: string }) =>
  api.get(`${base}/gaps`, { params }).then((r) => r.data.data)

export const fetchInterventions = () =>
  api.get<{ success: boolean; data: unknown[] }>(`${base}/interventions`).then((r) => r.data.data)

export const createIntervention = (body: Record<string, unknown>) =>
  api.post(`${base}/interventions`, body).then((r) => r.data.data)

export function useIntelligenceDashboard() {
  return useQuery({ queryKey: ['acdmc', 'intelligence', 'dashboard'], queryFn: fetchIntelligenceDashboard })
}

export function useAttainment(params: { className: string; section: string; subjectName: string } | null) {
  return useQuery({
    queryKey: ['acdmc', 'intelligence', 'attainment', params],
    queryFn: () => fetchAttainment(params!),
    enabled: Boolean(params?.className && params?.subjectName),
  })
}

export function useCreateIntervention() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createIntervention,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['acdmc', 'intelligence'] }),
  })
}
